import { useInkind } from '@gik/api/inkinds/inkind';
import { dotnetApi } from '@gik/core/api/ky/dotnetApi';
import type { User } from '@gik/core/models/gik/User';
import type { StreamParticipant } from '@gik/stream/types/StreamParticipant';
import React from 'react';

/**
 * Get a list of all organizers and the owner of an inkind page
 */
export const useInkindParticipants = (inkindRouteId: string) => {
  const [participants, setParticipants] = React.useState<StreamParticipant[]>([]);

  const { data: inkind } = useInkind(inkindRouteId);

  React.useEffect(() => {
    if (!inkind) return;

    (async () => {
      const organizerIds = inkind.organizers.map(organizer => organizer.userId);
      organizerIds.unshift(inkind.owner.userId);

      const userObjects: User[] = [];
      for (const userId of organizerIds) {
        const response = await dotnetApi.get(`users/${userId}`);
        if (response) userObjects.push(await response.json());
      }

      const pageOrganizerSuggestions: StreamParticipant[] = inkind.organizers.map(organizer => {
        const user = userObjects.find(u => u.id === organizer.userId);
        return {
          id: organizer?.userId,
          display: user?.fullName || organizer?.name,
          userAvatarThumbSmallURL: user?.avatarThumbSmallURL || organizer?.imgThumbSmall,
          userAvatarThumbMediumURL: user?.avatarThumbMediumURL || organizer?.imgThumbMedium,
        };
      });

      if (inkind.owner) {
        const user = userObjects.find(u => u.id === inkind.owner.userId);
        pageOrganizerSuggestions.unshift({
          id: inkind.owner.userId,
          display: inkind.owner.name || user?.fullName,
          userAvatarThumbSmallURL: inkind.owner.imgThumbSmall || user?.avatarThumbSmallURL,
          userAvatarThumbMediumURL: inkind.owner.imgThumbMedium || user?.avatarThumbMediumURL,
        });
      }

      setParticipants(pageOrganizerSuggestions);
    })();
  }, [inkind]);

  return participants;
};
